import QRCodeStyling from "qr-code-styling";

// ─── Types (self-contained duplicates of QREditor types) ─────────────────────

type Anchor = "top-left" | "top-right" | "bottom-left" | "bottom-right" | "center";

interface QRPlacement {
  anchor: Anchor;
  offsetX: number;
  offsetY: number;
  size: number;
  rotation: 0 | 90 | 180 | 270;
  transparentBg: boolean;
}

interface QRSettings {
  dotsType: string;
  cornersSquareType: string;
  cornersDotType: string;
  bodyColor: string;
  bgColor: string;
  logo: string;
  logoSize: number;
  colorizedLogo: boolean;
  placement: QRPlacement;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PREVIEW_W = 400;

/**
 * Must match QREditor.tsx's QR_SVG_BASE.
 * We render the QR at this base size and scale it to the target size,
 * exactly as the preview does (CSS scale from QR_SVG_BASE to placement.size).
 * Using SVG mode ensures the QR pattern fills 100% of the container —
 * canvas mode quantises module widths to integers and leaves a small quiet-zone
 * gap that makes the downloaded QR look slightly smaller than the preview.
 */
const QR_RENDER_BASE = 300;

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_PLACEMENT: QRPlacement = {
  anchor: "bottom-right",
  offsetX: 0,
  offsetY: 0,
  size: 150,
  rotation: 0,
  transparentBg: false,
};

const DEFAULT_SETTINGS: QRSettings = {
  dotsType: "rounded",
  cornersSquareType: "",
  cornersDotType: "",
  bodyColor: "#000000",
  bgColor: "#ffffff",
  logo: "",
  logoSize: 0.25,
  colorizedLogo: false,
  placement: { ...DEFAULT_PLACEMENT },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mergeSettings(saved: any): QRSettings {
  if (!saved) return { ...DEFAULT_SETTINGS, placement: { ...DEFAULT_PLACEMENT } };
  return {
    ...DEFAULT_SETTINGS,
    ...saved,
    placement: { ...DEFAULT_PLACEMENT, ...(saved.placement ?? {}) },
  };
}

function computeQRTopLeft(p: QRPlacement, logicalH: number): { left: number; top: number } {
  const { anchor, offsetX, offsetY, size } = p;
  switch (anchor) {
    case "top-left":     return { left: offsetX,                    top: offsetY };
    case "top-right":    return { left: PREVIEW_W - offsetX - size, top: offsetY };
    case "bottom-left":  return { left: offsetX,                    top: logicalH - offsetY - size };
    case "bottom-right": return { left: PREVIEW_W - offsetX - size, top: logicalH - offsetY - size };
    case "center":       return { left: PREVIEW_W / 2 - size / 2 + offsetX, top: logicalH / 2 - size / 2 + offsetY };
  }
}

/** Colorize a monochrome SVG by injecting a style rule over every shape element. */
function colorizeSvg(svgText: string, color: string): string {
  const style = `<style>path,rect,circle,ellipse,polygon,polyline,line{fill:${color} !important;stroke:none !important}</style>`;
  return svgText.replace(/(<svg[^>]*>)/i, `$1${style}`);
}

/** Load an image from a URL or data URI. */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith("data:")) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src.slice(0, 60)}`));
    img.src = src;
  });
}

/** Convert a Blob to an HTMLImageElement via object URL. */
function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Failed to decode QR blob")); };
    img.src = url;
  });
}

// ─── Core compositing ─────────────────────────────────────────────────────────

/**
 * Composites a card image (full resolution) with the QR code onto an offscreen
 * canvas and returns it. Callers can then download the canvas as PNG or embed
 * it in a PDF.
 *
 * - `cardImageUrl` null → white 400×400 background (QR-only mode).
 * - `rawQrSettings` null → card image only, no QR overlay.
 * - `dataUrl` overrides the encoded URL (used for editor preview).
 */
export async function compositeCardDesign(
  cardId: string,
  cardImageUrl: string | null,
  rawQrSettings: any | null,
  dataUrl?: string,
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  // 1. Establish canvas dimensions from the card image (or fallback 400×400)
  let imgW: number;
  let imgH: number;
  let cardImg: HTMLImageElement | null = null;

  if (cardImageUrl) {
    cardImg = await loadImage(cardImageUrl);
    imgW = cardImg.naturalWidth;
    imgH = cardImg.naturalHeight;
  } else {
    imgW = 400;
    imgH = 400;
  }

  canvas.width  = imgW;
  canvas.height = imgH;

  // 2. Draw background (card image or white fill)
  if (cardImg) {
    ctx.drawImage(cardImg, 0, 0);
  } else {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, imgW, imgH);
  }

  // 3. Overlay QR if settings are present
  if (rawQrSettings) {
    const settings = mergeSettings(rawQrSettings);
    const { placement } = settings;

    // Map from the 400-px logical coordinate space to real image pixels
    const imageScale = imgW / PREVIEW_W;
    const logicalH   = imgH / imageScale;
    const qrSizePx   = Math.round(placement.size * imageScale);
    const { left: qrLeft400, top: qrTop400 } = computeQRTopLeft(placement, logicalH);
    const qrLeft = Math.round(qrLeft400 * imageScale);
    const qrTop  = Math.round(qrTop400  * imageScale);

    // Render QR as SVG at QR_RENDER_BASE — pure vector, no external refs
    const qrData = dataUrl ?? `https://ar.arkheops.com/view/${cardId}`;
    const qr = new QRCodeStyling({
      width:  QR_RENDER_BASE,
      height: QR_RENDER_BASE,
      type: "svg" as any,
      data: qrData,
      // No logo here — added manually below to avoid canvas taint
      dotsOptions: { type: settings.dotsType as any, color: settings.bodyColor },
      cornersSquareOptions: {
        ...(settings.cornersSquareType ? { type: settings.cornersSquareType as any } : {}),
        color: settings.bodyColor,
      },
      cornersDotOptions: {
        ...(settings.cornersDotType ? { type: settings.cornersDotType as any } : {}),
        color: settings.bodyColor,
      },
      backgroundOptions: { color: placement.transparentBg ? "transparent" : settings.bgColor },
      qrOptions: { errorCorrectionLevel: "H" },
    });

    const qrBlob = await qr.getRawData("svg") as Blob | null;
    if (!qrBlob) throw new Error("QR generation returned no data");
    const qrImg = await blobToImage(qrBlob);

    // Apply rotation around the QR centre (same as preview transform)
    const cx = qrLeft + qrSizePx / 2;
    const cy = qrTop  + qrSizePx / 2;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((placement.rotation * Math.PI) / 180);

    // Scale SVG from QR_RENDER_BASE → qrSizePx (mirrors CSS scale(size/300) in preview)
    ctx.drawImage(qrImg, -qrSizePx / 2, -qrSizePx / 2, qrSizePx, qrSizePx);

    // Draw logo manually if present (margin: 6 matching style-tab options)
    if (settings.logo || settings.colorizedLogo) {
      const logoSize = Math.round(qrSizePx * settings.logoSize);
      const margin   = Math.round(6 * qrSizePx / QR_RENDER_BASE);
      const bgSize   = logoSize + margin * 2;
      try {
        let logoSrc = settings.logo;
        if (settings.colorizedLogo) {
          const resp = await fetch("/LogoImg.svg");
          const svgText = await resp.text();
          const colored = colorizeSvg(svgText, settings.bodyColor);
          logoSrc = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(colored)));
        }
        const logoImg = await loadImage(logoSrc);
        ctx.fillStyle = settings.bgColor;
        ctx.fillRect(-bgSize / 2, -bgSize / 2, bgSize, bgSize);
        ctx.drawImage(logoImg, -logoSize / 2, -logoSize / 2, logoSize, logoSize);
      } catch {
        // Logo load failed — skip it silently
      }
    }

    ctx.restore();
  }

  return canvas;
}

// ─── PNG download helper ──────────────────────────────────────────────────────

/**
 * Composites the scene's card image with the QR code and triggers a PNG download.
 */
export async function generateCardDesign(
  cardId: string,
  cardImageUrl: string,
  rawQrSettings: any,
  sceneName: string,
  dataUrl?: string,
): Promise<void> {
  const canvas = await compositeCardDesign(cardId, cardImageUrl, rawQrSettings, dataUrl);

  await new Promise<void>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error("canvas.toBlob() produced no output")); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sceneName}-${cardId}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      resolve();
    }, "image/png");
  });
}

